import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getPhaseData } from '../../data/programData';
import { getTestOutExercises } from '../../data/testOutExercises';
import { 
  ClipboardCheck, 
  Dumbbell, 
  Heart,
  ChevronRight,
  CheckCircle,
  Play,
  AlertCircle,
} from 'lucide-react';
import { Button } from '../../components/ui';

const TEST_OUT_STORAGE_KEY = 'acl_care_test_out_progress';

export function TestOutPage() {
  const { phaseId } = useParams();
  const navigate = useNavigate();
  const { aclCare } = useAuth();

  const [warmupType, setWarmupType] = useState(null); // 'movement' | 'cardio'
  const [exerciseType, setExerciseType] = useState(null); // 'machine' | 'non-machine'
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [existingSession, setExistingSession] = useState(null);

  const phaseNum = parseInt(phaseId);
  const phaseData = getPhaseData(phaseNum);
  const phaseKey = `phase_${phaseNum}`;
  const userPhaseData = aclCare?.phases?.[phaseKey];
  const isTestOutPassed = userPhaseData?.test_out_passed;
  const testOutAttempts = userPhaseData?.test_out_attempts || 0;

  // Check for existing session on mount
  useEffect(() => {
    const stored = localStorage.getItem(TEST_OUT_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        const today = new Date().toDateString();
        if (parsed.date === today && parsed.phaseNum === phaseNum) {
          setExistingSession(parsed);
          setShowResumeModal(true);
        }
      } catch (e) {
        console.error('Error parsing stored test out session:', e);
      }
    }
  }, [phaseNum]);

  // Get exercises to show count
  const machineExercises = getTestOutExercises(phaseNum, true);
  const nonMachineExercises = getTestOutExercises(phaseNum, false);

  const handleStartTestOut = () => {
    if (!warmupType || !exerciseType) return;

    // Initialize new session in localStorage
    const newSession = {
      date: new Date().toDateString(),
      phaseNum,
      warmupType,
      exerciseType: exerciseType === 'machine',
      phase: 'warmup',
      warmupCompleted: false,
      currentExerciseIndex: 0,
      exerciseData: {},
      completed: false,
      startedAt: new Date().toISOString(),
    };

    localStorage.setItem(TEST_OUT_STORAGE_KEY, JSON.stringify(newSession));
    navigate(`/program/phase/${phaseId}/test-out/play`);
  };

  const handleContinueSession = () => {
    setShowResumeModal(false);
    navigate(`/program/phase/${phaseId}/test-out/play`);
  };

  const handleViewOverview = () => {
    // Restore selections from existing session
    if (existingSession) {
      setWarmupType(existingSession.warmupType);
      setExerciseType(existingSession.exerciseType ? 'machine' : 'non-machine');
    }
    setShowResumeModal(false);
  };

  const handleStartNew = () => {
    localStorage.removeItem(TEST_OUT_STORAGE_KEY);
    setExistingSession(null);
    setShowResumeModal(false);
    setWarmupType(null);
    setExerciseType(null);
  };

  if (!phaseData) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <p className="text-gray-500">Phase not found</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-4 pb-24 lg:pb-8">
      {/* Header */}
      <div className="flex items-center mb-6">
        <ClipboardCheck className="h-6 w-6 text-primary mr-2" />
        <h1 className="text-2xl font-bold text-gray-900">
          {phaseData.title} - Test Out
        </h1>
      </div>

      {/* Previous Pass Banner */}
      {isTestOutPassed && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
          <div className="flex items-start">
            <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 mr-3" />
            <div>
              <h3 className="font-medium text-green-800">Previously Passed</h3>
              <p className="text-sm text-green-700 mt-1">
                You've passed this test out. You can retake it anytime to track your progress.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Attempt Count */}
      {testOutAttempts > 0 && (
        <p className="text-sm text-gray-500 mb-4">
          Previous attempts: {testOutAttempts}
        </p>
      )}

      {/* Warmup Type Selection */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4">
        <h2 className="font-semibold text-gray-900 mb-3">Select Warmup Type</h2>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => setWarmupType('movement')}
            className={`p-4 rounded-xl border-2 text-left transition-all ${
              warmupType === 'movement'
                ? 'border-primary bg-primary/5'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className={`h-10 w-10 rounded-full flex items-center justify-center mb-2 ${
              warmupType === 'movement' ? 'bg-primary/10' : 'bg-gray-100'
            }`}>
              <Dumbbell className={`h-5 w-5 ${
                warmupType === 'movement' ? 'text-primary' : 'text-gray-500'
              }`} />
            </div>
            <h3 className="font-medium text-gray-900">Movement Warmup</h3>
            <p className="text-xs text-gray-500 mt-1">
              Dynamic stretches and exercises
            </p>
          </button>

          <button
            onClick={() => setWarmupType('cardio')}
            className={`p-4 rounded-xl border-2 text-left transition-all ${
              warmupType === 'cardio'
                ? 'border-primary bg-primary/5'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className={`h-10 w-10 rounded-full flex items-center justify-center mb-2 ${
              warmupType === 'cardio' ? 'bg-primary/10' : 'bg-gray-100'
            }`}>
              <Heart className={`h-5 w-5 ${
                warmupType === 'cardio' ? 'text-primary' : 'text-gray-500'
              }`} />
            </div>
            <h3 className="font-medium text-gray-900">Cardio Warmup</h3>
            <p className="text-xs text-gray-500 mt-1">
              10 min on equipment of choice
            </p>
          </button>
        </div>
      </div>

      {/* Exercise Type Selection */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <h2 className="font-semibold text-gray-900 mb-3">Select Exercise Type</h2>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => setExerciseType('non-machine')}
            className={`p-4 rounded-xl border-2 text-left transition-all ${
              exerciseType === 'non-machine'
                ? 'border-primary bg-primary/5'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <h3 className="font-medium text-gray-900">Non-Machine</h3>
            <p className="text-xs text-gray-500 mt-1">
              {nonMachineExercises.length} exercises
            </p>
            {exerciseType === 'non-machine' && (
              <CheckCircle className="h-5 w-5 text-primary mt-2" />
            )}
          </button>

          <button
            onClick={() => setExerciseType('machine')}
            className={`p-4 rounded-xl border-2 text-left transition-all ${
              exerciseType === 'machine'
                ? 'border-primary bg-primary/5'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <h3 className="font-medium text-gray-900">Machine</h3>
            <p className="text-xs text-gray-500 mt-1">
              {machineExercises.length} exercises
            </p>
            {exerciseType === 'machine' && (
              <CheckCircle className="h-5 w-5 text-primary mt-2" />
            )}
          </button>
        </div>
      </div>

      {/* Exercise Preview */}
      {exerciseType && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <h2 className="font-semibold text-gray-900 mb-3">Exercises</h2>
          <div className="space-y-2">
            {(exerciseType === 'machine' ? machineExercises : nonMachineExercises).map((exercise, index) => (
              <div 
                key={exercise.id}
                className="flex items-center py-2 border-b border-gray-100 last:border-0"
              >
                <span className="text-sm text-gray-400 w-6">{index + 1}.</span>
                <span className="text-sm text-gray-700">{exercise.displayName}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Start Button */}
      <Button
        onClick={handleStartTestOut}
        disabled={!warmupType || !exerciseType}
        fullWidth
        size="lg"
      >
        <Play className="h-5 w-5 mr-2" />
        Start Test Out
      </Button>

      {/* Info Note */}
      <div className="mt-4 flex items-start text-sm text-gray-500">
        <AlertCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
        <p>
          You'll complete the warmup first, then proceed through each test exercise. 
          Document only the highest level you perform.
        </p>
      </div>

      {/* Resume Session Modal */}
      {showResumeModal && existingSession && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/50" />
          
          <div className="flex min-h-full items-end justify-center p-4 sm:items-center">
            <div className="relative bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                Test Out In Progress
              </h2>
              <p className="text-gray-600 mb-6">
                You have an unfinished test out from today. What would you like to do?
              </p>

              <div className="space-y-3">
                <Button
                  onClick={handleContinueSession}
                  fullWidth
                >
                  Continue Where I Left Off
                </Button>
                <Button
                  variant="outline"
                  onClick={handleViewOverview}
                  fullWidth
                >
                  View Overview
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleStartNew}
                  fullWidth
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  Start New Test Out
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}